import React, { useState } from 'react';
import {
  Container,
  Text,
  Group,
  Select,
  MultiSelect,
  Paper,
  SimpleGrid,
  Skeleton,
  Alert,
  Stack
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ReportListCard } from './ReportListCard';
import { PageHeader } from '../common/PageHeader';
import { useGroupQuery } from '../../hooks/useGroupQuery';
import { reportsApi } from '../../api/reports';

export function ReportsPage() {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Fetch all reports using useGroupQuery
  const { data: reports, isLoading, error } = useGroupQuery(
    ['reports'],
    reportsApi.getAllReports
  );

  // Filter reports based on selected criteria
  const filteredReports = reports?.filter(report => {
    const matchesYear = !selectedYear || dayjs(report.dueDate).format('YYYY') === selectedYear;
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(report.status);
    return matchesYear && matchesStatus;
  }).sort((a, b) => dayjs(b.dueDate).valueOf() - dayjs(a.dueDate).valueOf()) || [];

  // Get unique years from all reports
  const availableYears = Array.from(new Set(reports?.map(report => 
    dayjs(report.dueDate).format('YYYY')
  ) || []));

  // Status options for filter
  const statusOptions = [
    { value: 'not_submitted', label: 'Not Submitted' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'in_review', label: 'In Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const handleSubmit = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleView = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleUpdate = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleYearChange = (value: string | null): void => {
    setSelectedYear(value);
  };

  const handleStatusChange = (value: string[]): void => {
    setSelectedStatuses(value);
  };

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Reports"
        subtitle="View and manage quarterly reports. Filter by year or status to find specific reports."
      />

      {/* Filters */}
      <Paper shadow="sm" p="md" mb="xl" withBorder sx={(theme) => ({
        maxWidth: 800,
        '@media (max-width: 768px)': {
          margin: theme.spacing.sm,
          padding: theme.spacing.sm,
          maxWidth: 'none'
        }
      })}>
        <Group spacing="md" sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
          }
        })}>
          <Select
            label="Filter by Year"
            placeholder="Select year"
            value={selectedYear}
            onChange={handleYearChange}
            clearable
            data={availableYears.map(year => ({ value: year, label: year }))}
            disabled={isLoading}
            style={{ flex: 1, minWidth: 200 }}
            styles={(theme) => ({
              label: {
                marginBottom: '8px'
              },
              root: {
                '@media (max-width: 768px)': {
                  width: '100%',
                  minWidth: 'auto'
                }
              }
            })}
          />
          <MultiSelect
            label="Filter by Status"
            placeholder="Select statuses"
            value={selectedStatuses}
            onChange={handleStatusChange}
            clearable
            data={statusOptions}
            disabled={isLoading}
            style={{ flex: 1, minWidth: 200 }}
            styles={(theme) => ({
              label: {
                marginBottom: '8px'
              },
              root: {
                '@media (max-width: 768px)': {
                  width: '100%',
                  minWidth: 'auto'
                }
              }
            })}
          />
        </Group>
      </Paper>

      {error ? (
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          title="Error" 
          color="red"
          role="alert"
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            }
          })}
        >
          Failed to load reports. Please try again later.
        </Alert>
      ) : null}

      {/* Reports Grid */}
      <SimpleGrid
        cols={3}
        spacing="lg"
        breakpoints={[
          { maxWidth: 'md', cols: 2 },
          { maxWidth: 'sm', cols: 1 },
          { maxWidth: 480, cols: 1, spacing: 'md' }
        ]}
        sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
          }
        })}
      >
        {isLoading ? (
          Array(6).fill(0).map((_, index) => (
            <Paper key={index} p="md" withBorder>
              <Stack spacing="md">
                <Skeleton height={24} width="70%" radius="md" />
                <Skeleton height={20} width="40%" radius="md" />
                <Group position="apart" mt="md" sx={(theme) => ({
                  '@media (max-width: 480px)': {
                    flexDirection: 'column',
                    gap: theme.spacing.sm
                  }
                })}>
                  <Skeleton height={32} width={100} radius="md" />
                  <Skeleton height={32} width={100} radius="md" />
                </Group>
              </Stack>
            </Paper>
          ))
        ) : error ? null : filteredReports.length > 0 ? (
          filteredReports.map(report => (
            <ReportListCard
              key={report.id}
              report={report}
              onSubmit={handleSubmit}
              onView={handleView}
              onUpdate={handleUpdate}
            />
          ))
        ) : (
          <Text align="center" color="dimmed" style={{ gridColumn: '1 / -1' }}>
            No reports match the selected filters.
          </Text>
        )}
      </SimpleGrid>
    </Container>
  );
} 